// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import type {MachineCollider,MachineDefinition,MachineToy,PolylineCollider} from '@core/machine'

/** Footprints of the mounted portal mesh; never a filled disk across its shot opening. */
export function portalMounts(toy:MachineToy):readonly PolylineCollider[]{
  if(!toy||toy.kind!=='portal'||!Array.isArray(toy.centre)||toy.centre.length!==2||!toy.centre.every(Number.isFinite))return []
  const round=(n:number)=>Number(n.toFixed(7)),[cx,cy]=toy.centre
  return [-1,1].map(side=>{
    const x=cx+(side<0?-.042:.034),x0=round(x-.009),x1=round(x+.009),y0=round(cy-.01),y1=round(cy+.01)
    return {id:toy.id+(side<0?'MountLeft':'MountRight'),kind:'polyline',layer:toy.layer,
      points:[[x0,y0],[x1,y0],[x1,y1],[x0,y1],[x0,y0]],material:'metal'}
  })
}

/** The exported mesh already draws these feet. Altered/custom contacts retain their visible rails. */
export function ownsPortalMount(definition:MachineDefinition,collider:MachineCollider):boolean{
  if(!collider.id.endsWith('MountLeft')&&!collider.id.endsWith('MountRight'))return false
  if(collider.kind!=='polyline'||collider.material!=='metal'||(collider.thickness??0)!==0||collider.device!==undefined||collider.adjustedBy!==undefined||collider.outlane!==undefined)return false
  return (definition.toys??[]).some(toy=>portalMounts(toy).some(expected=>expected.id===collider.id&&expected.layer===collider.layer
    &&expected.points.length===collider.points.length&&expected.points.every((p,i)=>Math.abs(p[0]-collider.points[i]![0])<1e-8&&Math.abs(p[1]-collider.points[i]![1])<1e-8)))
}

/** Editable drafts opt in; loading historical scored definitions never calls this upgrade. */
export function portalMountsForEditing(definition:MachineDefinition):MachineDefinition{
  if(!definition||!Array.isArray(definition.colliders)||!Array.isArray(definition.toys))return definition
  if(!definition.toys.some(t=>t?.kind==='portal'))return definition
  // Migrate only the unchanged shipped pose; preserve user-positioned arches.
  const toys=definition.toys.map(t=>definition.machineId==='starfall-salvage'&&t?.id==='jumpPortal'&&t.kind==='portal'
    &&Array.isArray(t.centre)&&t.centre[0]===.101&&t.centre[1]===.019&&t.radius===.04&&t.height===.086
    ?{...t,centre:[.118,.089] as const,height:.107}:t)
  const taken=new Set(definition.colliders.map(c=>c.id)),missing=toys.flatMap(portalMounts).filter(c=>{
    if(taken.has(c.id))return false
    taken.add(c.id);return true
  })
  const relocated=toys.some((t,i)=>t!==definition.toys![i])
  return missing.length||relocated?{...definition,toys,colliders:[...definition.colliders,...missing]}:definition
}
